import { useQuery } from '@apollo/client';
import { Box, useTheme } from '@mui/material';
import { useState } from 'react';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import { ParticipationHistoryTable } from '@/components/SummaryList/ParticipationHistoryTable';
import { HISTORICAL_PARTICIPATIONS_QUERY } from '@/pages/OilAndGasMap/Info/operations';
import type { Participation } from '@/types/GraphqlSchema';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'historical_participation_table';

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<Participation[]>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  const { loading, error } = useQuery(HISTORICAL_PARTICIPATIONS_QUERY, {
    variables: {
      entityId: portfolio.assets?.length ? portfolio.assets[0] || 0 : 0,
      entityType: 'Asset',
      grouped: true,
      perPage: 45,
      orderBy: 'effectiveDate',
      order: 'asc',
    },
    skip: !!persistedData || !portfolio.assets?.length,
    onCompleted: response => {
      if (response?.participations) {
        const data = response.participations.participations;
        setDataFetched(data);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data,
          });
        }
      }
    },
  });

  return { data: dataFetched, error, loading };
};

const ComponentView = ({ data }: ComponentViewProps<Participation[]>) => {
  const theme = useTheme();

  if (!data) return null;

  const customStyles = {
    participationCell: {
      fontFamily: theme.fonts.inter,
    },
    participationHeaderCell: {
      fontFamily: theme.fonts.inter,
    },
  };

  return (
    <Box
      display='flex'
      justifyContent='center'
      width='100%'
      height='100%'
      maxWidth='100%'
      maxHeight='100%'
      overflow='auto'
    >
      <ParticipationHistoryTable
        historicalParticipation={data}
        moreParticipationThanDisplayed={false}
        customStyles={customStyles}
      />
    </Box>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<Participation[]>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      {data && <ComponentView data={data} />}
    </LoadingErrorWrapper>
  );
};

export const HistoricalParticipationTableComponent: ComponentDefinition<Participation[]> = {
  id: COMPONENT_ID,
  name: 'Historical Participation',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset],
  Component: ComponentEdit,
  ComponentView,
};
